<template>
  <div
    class="xur-banner transition--color-bg"
    :class="{ 'xur-banner--subbed': isEmailSubbed }">
    <img
      class="xur-banner__background"
      src="/images/altcraft_popup/whereXurBack.jpg"
      alt="">

    <img
      class="xur-banner__image"
      src="/images/altcraft_popup/whereXurFront.png"
      alt="">

    <h1 class="xur-banner__header">
      {{ isEmailSubbed ? 'Thank you for subscribing!' : 'Never miss Xur location!' }}
    </h1>

    <p class="xur-banner__text">
      We'll notify when Xur arrives and let you know the new items and location
    </p>

    <ui-input
      v-model="email"
      :error="hasError"
      :z-index-tooltip="2147483005"
      :error-triggers="['hover']"
      class="xur-banner__input"
      type="email"
      placeholder="Enter your email"
      @focus="hasInteraction = true"
      @blur="hasInteraction = false"
      @keyup.native.enter="subEmail" />

    <button
      class="button xur-banner__sub-button"
      @click="subEmail">
      Subscribe
    </button>

    <altcraft-popup
      v-if="displayPushHint"
      type="pushPrompt"
      :email="emailForPopup"
      @close="displayPushHint = false" />
  </div>
</template>

<script>
import { regExpEmail } from 'Root/helpers/regexps';
import UiInput from 'Components/ui/Input/UiInput.vue';
import AltcraftPopupXur from 'Components/Popups/altcraftPopups/AltcraftPopupXur.vue';
import AltcraftPopup from 'Components/Popups/altcraftPopups/AltcraftPopup.vue';
import altcraftAnalytics from 'Root/drivers/altcraftAnalytics';
import AltcraftPopupModel from 'Models/AltcraftPopupModel';
import localStorageManager from 'Root/helpers/localStorageManager';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';

import { mapState } from 'vuex';

const LONG_TIME_MS = 20000;

export default {
  name: 'AltcraftBannerXur',

  components: {
    UiInput,
    AltcraftPopup,
  },

  data: () => ({
    email: '',
    hasInteraction: false,
    isEmailSubbed: false,
    displayPopup: false,
    displayPushHint: false,
  }),

  computed: {
    ...mapState(['userDevice']),

    hasError() {
      return !this.hasInteraction && !this.email.match(regExpEmail) && this.email.length > 0
        ? 'Please, enter a valid email'
        : false;
    },

    emailForPopup() {
      if (this.hasError || this.email.length < 1) {
        return null;
      }

      return (this.isEmailSubbed ? this.email : null) ?? this.user?.email ?? '';
    },
  },

  watch: {
    displayPopup() {
      if (localStorageManager.getFlag(AltcraftPopupModel.LOCALSTORAGE.XUR_PUSH_SUBSCRIBED)) {
        return;
      }

      this.$vuedals.openMobilePanel({
        class: 'xur-popup__wrapper',
        component: AltcraftPopupXur,
        onClose: (isSubButton) => {
          if (isSubButton
              && Notification.permission !== 'granted'
              && !(this.userDevice.isSafari || this.userDevice.innerWidth < 1280)) {
            this.displayPushHint = true;
          }
        },
      });
    },
  },

  mounted() {
    this.email = this.user?.email ?? '';
    this.isEmailSubbed = localStorageManager.getCooldown(AltcraftPopupModel.LOCALSTORAGE.XUR_EMAIL_COOLDOWN);

    setTimeout(() => {
      this.displayPopup = true;
    }, LONG_TIME_MS);

    document.addEventListener('scroll', this.scrollHandler);
  },

  beforeDestroy() {
    document.removeEventListener('scroll', this.scrollHandler);
  },

  methods: {
    scrollHandler() {
      if (window.scrollY > window.innerHeight) {
        this.displayPopup = true;
      }
    },

    subEmail() {
      if (this.hasError || this.email.length < 1) {
        return;
      }

      altcraftAnalytics.subscribeToEmailChannel(this.email, process.env.ALTCRAFT_RESOURCE_XUR_ID);

      this.isEmailSubbed = true;

      googleTagAnalytics.emitXurPopup('email');
    },
  },
};
</script>

<style lang="scss" scoped>
.xur-banner {
  font-family: Rubik, sans-serif;
  padding: 24px;
  background-color: $color--basic-black;
  position: relative;
  box-sizing: border-box;
  background-clip: padding-box;
  border: solid 3px transparent;
  border-radius: 24px;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    border-radius: 21px;
    box-shadow: inset 0 0 0px 3px rgba($color--basic-blue, 0.25);
    z-index: 1;
  }
}

.xur-banner__background {
  @include fill;

  pointer-events: none;
  opacity: .5;
}

.xur-banner__header {
  font-size: 21px;
  font-weight: 500;
  margin-bottom: 16px;
  position: relative;
}

.xur-banner__text {
  font-size: 15px;
  font-weight: 300;
  margin-bottom: 16px;
  position: relative;
}

.xur-banner__sub-button {
  padding: 12px;
  width: 100%;
  position: relative;
  background: linear-gradient(270deg, $color--basic-green 0%, $color--basic-lime 50%, $color--basic-lime 100%) 100% 0%;
  background-size: 200% 100%;
  transition: background-position var(--transition-color-time) var(--transition-color-easing);
}

.xur-banner__sub-button:hover {
  background-position: 0% 0%;
}

.xur-banner__input {
  --height: 40px;
  --padding: 13px;
  --font-size: 13px;
  --color: #{$color--basic-white};
  --placeholder-color: #{$color--basic-white};
  --background-color: #{rgba($color--basic-blue, .25)};
  --background-color--focused: #{rgba($color--basic-blue, .25)};
  --border-radius: 4px;

  width: 100%;
  margin-bottom: 16px;

  /deep/.ui-input__wrap-inner {
    border: 2px solid rgba($color--basic-blue, .25);
  }
}

.xur-banner__image {
  height: 280px;
  position: absolute;
  bottom: 0;
  pointer-events: none;
  right: calc((-100px - 275px * 0.25) + 25%);
  // 100px - общий сдвиг для картинки; 25 - константа коэффа скорости трансформации
  // 220 * 1.25 = 275px - сдвиг, подбирается с учётом выбранной константы, 220 идеальное значение для мобильного брейка
}

.xur-banner--subbed {
  background-color: $color--basic-blue;
}

.xur-banner--subbed .xur-banner__header {
  margin-bottom: 0;
}

.xur-banner--subbed .xur-banner__image {
  top: -35px;
}

.xur-banner--subbed .xur-banner__text,
.xur-banner--subbed .xur-banner__sub-button,
.xur-banner--subbed .xur-banner__input {
  display: none;
}

@media screen and #{$screen-more-or-1024} {
  .xur-banner {
    padding: 40px;
  }

  .xur-banner__header {
    font-size: 35px;
    max-width: 50%;
  }

  .xur-banner__text {
    font-size: 19px;
    max-width: 50%;
  }

  .xur-banner__sub-button {
    padding: 15px;
    width: unset;
    margin-left: 16px;
  }

  .xur-banner__input {
    --padding: 15px;
    --font-size: 15px;
    --height: 48px;

    display: inline-block;
    margin-bottom: 0;
    width: unset;
    min-width: 320px;
  }

  .xur-banner__image {
    top: 0;
    height: 325px;
  }
}
</style>
